import { ref } from "vue";
import {
  API_SERVICE_INJECTION_KEY,
  ERROR_SERVICE_INJECTION_KEY,
} from "@/constants";
import { shouldRedirectToSessionExpired } from "@/modules/app/utils/should-redirect-to-session-expired.util";
import { redirectToSessionExpiredPage } from "@/modules/app/utils/session-expired-navigation.util";
import { injector } from "@/services";
import { getSession, getOnboardingModel } from "../../../../api/index";
import { IOnboardingModel, IOnboardingSession } from "../../../../../types";

const AUTH_HEADER_NAME = "X-AccessToken";
export const useStateFeature = () => {
  const token = ref<string | null>(null);
  const sessionId = ref<string | null>(null);
  const isCompletedInvite = ref<boolean>(false);
  const setToken = (value: string | null) => {
    token.value = value;
  };

  const initSession = async () => {
    let session: IOnboardingSession | null = null;
    isCompletedInvite.value = false;

    if (!token.value) {
      return session;
    }

    const apiService = injector.injectFromRoot(API_SERVICE_INJECTION_KEY);
    const errorService = injector.injectFromRoot(ERROR_SERVICE_INJECTION_KEY);

    errorService.blockDisplay();
    try {
      const result = await getSession(token.value);
      session = result.session;

      if (result.isCompletedInvite) {
        isCompletedInvite.value = true;
        sessionId.value = null;
        return session;
      }

      if (shouldRedirectToSessionExpired(token.value, session)) {
        await redirectToSessionExpiredPage();
        return null;
      }

      const config = apiService.config;
      if (session && config?.headers) {
        config.headers[AUTH_HEADER_NAME] = session.session_id;
        sessionId.value = session.session_id;
        apiService.config = config;
      }
    } finally {
      if (session || isCompletedInvite.value) {
        errorService.unblockDisplay();
      }
    }

    return session;
  };

  const model = ref<IOnboardingModel | null>(null);

  const getModel = async () => {
    model.value = await getOnboardingModel();
    return model.value;
  };

  return {
    token,
    sessionId,
    isCompletedInvite,
    setToken,
    initSession,
    model,
    getModel,
  };
};
